import { useState, useEffect, useMemo, type JSX } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Badge, TemplateModal, type TemplateFormData, BandModal, type BandFormData, Skeleton, ConfirmDialog, DashboardAnalytics, Modal, RichTextEditor } from '../components/Common';
import { authAPI, emailTemplatesAPI, bandsAPI, venuesAPI, csvImportAPI } from '../api/endpoints';
import { Settings, Mail, LogOut, User as UserIcon, Music, AlertCircle, CheckCircle, Trash2, Download, Database, Moon, Sun, Eye, Sparkles, Flower2, Flame, Star, TreePine, Sunset, Upload, Send, Plus, X, GripVertical, RotateCcw, Pencil, BarChart3, Slash } from 'lucide-react';
import type { Band, EmailSnippet, EmailTemplate } from '../types';
import {
  MAX_SNIPPETS,
  MAX_SNIPPET_HTML_LENGTH,
  MAX_SNIPPET_TAG_LENGTH,
  snippetLabel,
} from '../utils/snippets';
import { useAutoSyncEnabled } from '../hooks/useEmailSync';
import { FALLBACK_TODO_DAYS, MAX_TODO_DAYS } from '../hooks/useDefaultTodoDays';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, type Theme } from '../contexts/ThemeContext';
import { getBrowserTimezone, listTimezones, resolveUserTimezone, timezoneOffsetLabel } from '../utils/timezone';
import { toEditableHtml, toStoredHtml } from '../utils/emailImages';

const themeOptions: { value: Theme; label: string; description: string; icon: JSX.Element }[] = [
  { value: 'dark', label: 'Dark', description: 'Easy on the eyes', icon: <Moon className="w-5 h-5" /> },
  { value: 'light', label: 'Light', description: 'Classic bright look', icon: <Sun className="w-5 h-5" /> },
  { value: 'fun', label: 'Fun', description: 'Purple vibes', icon: <Sparkles className="w-5 h-5" /> },
  { value: 'pastel', label: 'Pastel', description: 'Soft & dreamy', icon: <Flower2 className="w-5 h-5" /> },
  { value: 'rock', label: 'Rock', description: 'Heavy & bold', icon: <Flame className="w-5 h-5" /> },
  { value: 'midnight', label: 'Midnight', description: 'Navy & gold', icon: <Star className="w-5 h-5" /> },
  { value: 'forest', label: 'Forest', description: 'Deep & earthy', icon: <TreePine className="w-5 h-5" /> },
  { value: 'sunset', label: 'Sunset', description: 'Warm & cozy', icon: <Sunset className="w-5 h-5" /> },
  { value: 'high-contrast', label: 'High Contrast', description: 'Maximum readability', icon: <Eye className="w-5 h-5" /> },
];

function ThemeSelector() {
  const { theme, setTheme } = useTheme();

  return (
    <div>
      <h3 className="text-md font-semibold text-dark-text-primary mb-4">
        Appearance
      </h3>
      <div className="grid grid-cols-3 gap-3">
        {themeOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setTheme(opt.value)}
            className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all duration-200 ${
              theme === opt.value
                ? 'border-dark-accent bg-dark-accent/10 text-dark-accent'
                : 'border-dark-border bg-dark-elevated hover:bg-dark-border/50 text-dark-text-secondary hover:text-dark-text-primary'
            }`}
          >
            {opt.icon}
            <span className="text-sm font-medium">{opt.label}</span>
            <span className="text-xs text-dark-text-muted">{opt.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const DEFAULT_CHECKLIST = [
  "Send quote",
  "Agree on conditions (price, housing, catering)",
  "Send contract",
  "Send communication elements (bio, photos)",
  "Send tech rider",
  "Get roadmap",
  "Send roadmap to team",
];

/**
 * The default delay of the follow-up todo a sent email leaves behind — what seeds the
 * "Todo (days)" box in the contact overlay and the mailing-list batch modal (docs/EMAIL_SEND.md).
 *
 * Saves on blur or Enter rather than per keystroke, so typing "30" does not first store 3.
 */
function FollowUpTodoSetting({ days, onSave, isSaving }: {
  days: number;
  onSave: (days: number) => void;
  isSaving: boolean;
}) {
  const [value, setValue] = useState(String(days));

  const commit = () => {
    const parsed = Math.round(Number(value));
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > MAX_TODO_DAYS) {
      setValue(String(days)); // out of range — the API would reject it, so snap back
      return;
    }
    setValue(String(parsed));
    if (parsed !== days) onSave(parsed);
  };

  return (
    <div>
      <h3 className="text-md font-semibold text-dark-text-primary mb-4">
        Follow-up Todos
      </h3>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={MAX_TODO_DAYS}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            disabled={isSaving}
            className="w-24 px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500"
          />
          <span className="text-sm text-dark-text-secondary">
            day{days !== 1 ? 's' : ''} after the email goes out
          </span>
        </div>
        <p className="text-xs text-dark-text-muted">
          Fills the <span className="text-dark-text-secondary">Todo (days)</span> box when you write
          a mail or set up a batch send. You can still change it for an individual send, or tick
          Disable todo to create none.
        </p>
      </div>
    </div>
  );
}

function DefaultChecklistEditor({ checklist, onSave }: {
  checklist: string[];
  onSave: (items: string[]) => void;
}) {
  const [items, setItems] = useState<string[]>(checklist);
  const [newItem, setNewItem] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const save = (newItems: string[]) => {
    setItems(newItems);
    onSave(newItems);
  };

  const addItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;
    save([...items, trimmed]);
    setNewItem('');
  };

  const removeItem = (index: number) => {
    save(items.filter((_, i) => i !== index));
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(index, 0, moved);
    setItems(reordered);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex !== null) {
      onSave(items);
      setDragIndex(null);
    }
  };

  const handleReset = () => {
    save(DEFAULT_CHECKLIST);
    setShowResetConfirm(false);
  };

  return (
    <div>
      <h3 className="text-md font-semibold text-dark-text-primary mb-4 flex items-center gap-2">
        Default Concert Checklist
      </h3>
      <p className="text-xs text-dark-text-muted mb-4">
        These items will be pre-filled as the checklist when you create a new concert.
      </p>

      <div className="space-y-1 mb-3">
        {items.map((item, index) => (
          <div
            key={`${index}-${item}`}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-2 p-2 rounded-lg border border-dark-border bg-dark-elevated group ${
              dragIndex === index ? 'opacity-50' : ''
            }`}
          >
            <GripVertical className="w-4 h-4 text-dark-text-muted cursor-grab shrink-0" />
            <span className="text-sm text-dark-text-secondary flex-1">{item}</span>
            <button
              onClick={() => removeItem(index)}
              className="text-dark-text-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-dark-text-muted italic p-2">No checklist items. Add some below.</p>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
          placeholder="Add a checklist item..."
          className="input flex-1 text-sm"
        />
        <Button
          onClick={addItem}
          disabled={!newItem.trim()}
          variant="secondary"
          size="sm"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      <Button
        onClick={() => setShowResetConfirm(true)}
        variant="ghost"
        size="sm"
      >
        <RotateCcw className="w-3 h-3 mr-1" />
        Reset to defaults
      </Button>

      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title="Reset Checklist?"
        message="This will replace your current checklist with the default items."
        confirmLabel="Reset"
        danger
      />
    </div>
  );
}

/**
 * One entry of the "/" snippet library (docs/EMAIL_SNIPPETS.md): rich text written in the same
 * editor that will insert it, plus an optional tag the menu filters on first.
 *
 * Mounted per opening, so the form seeds itself from `snippet` on mount rather than resetting
 * through an effect.
 */
function SnippetModal({ snippet, onClose, onSubmit }: {
  snippet: EmailSnippet | null;
  onClose: () => void;
  onSubmit: (snippet: EmailSnippet) => void;
}) {
  const [tag, setTag] = useState(snippet?.tag ?? '');
  // Seeded through toEditableHtml so a snippet carrying a logo shows it (docs/EMAIL_IMAGES.md)
  const [html, setHtml] = useState(() => toEditableHtml(snippet?.html ?? ''));

  // The caps are the ones the API enforces: catching them here keeps a rejected save from
  // looking like nothing happened.
  const tooLong = html.length > MAX_SNIPPET_HTML_LENGTH;
  const canSave = snippetLabel({ tag: '', html }).length > 0 && !tooLong;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={snippet ? 'Edit Snippet' : 'New Snippet'}
      size="2xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSubmit({ tag: tag.trim(), html: toStoredHtml(html) })}
            disabled={!canSave}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Tag (optional)"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          placeholder="merci"
          maxLength={MAX_SNIPPET_TAG_LENGTH}
        />
        <p className="text-xs text-dark-text-muted -mt-2">
          A short keyword. Typing it after <code>/</code> finds this snippet even when the sentence
          itself does not contain the word.
        </p>
        <div>
          <label className="block text-sm font-medium text-dark-text-secondary mb-1">Text</label>
          {/* No "/" menu here: this is where a snippet is written, not where one is inserted. */}
          <RichTextEditor content={html} onChange={setHtml} enableSnippets={false} />
          {tooLong && (
            <p className="text-xs text-red-400 mt-1">
              Too long to save ({html.length} of {MAX_SNIPPET_HTML_LENGTH} characters, formatting
              included). Shorten it, or split it into two snippets.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function EmailSnippetsEditor({ snippets, onSave }: {
  snippets: EmailSnippet[];
  onSave: (snippets: EmailSnippet[]) => void;
}) {
  const [items, setItems] = useState<EmailSnippet[]>(snippets);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // The open snippet form: `index` is null while adding, and `seq` keys the form so each opening
  // mounts a fresh one.
  const [editing, setEditing] = useState<{ index: number | null; seq: number } | null>(null);

  const openForm = (index: number | null) => {
    setEditing((prev) => ({ index, seq: (prev?.seq ?? 0) + 1 }));
  };

  const save = (next: EmailSnippet[]) => {
    setItems(next);
    onSave(next);
  };

  const handleSubmit = (snippet: EmailSnippet) => {
    if (!editing) return;
    const { index } = editing;
    save(index === null ? [...items, snippet] : items.map((item, i) => (i === index ? snippet : item)));
    setEditing(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(index, 0, moved);
    setItems(reordered);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex !== null) {
      onSave(items);
      setDragIndex(null);
    }
  };

  return (
    <div>
      <h3 className="text-md font-semibold text-dark-text-primary mb-4 flex items-center gap-2">
        <Slash className="w-4 h-4" />
        Mail Snippets
      </h3>
      <p className="text-xs text-dark-text-muted mb-4">
        Sentences you reuse when writing mail. In any mail editor, typing <code>/</code> at the start
        of a word opens this list; keep typing to filter it (tags first), then pick with
        <span className="mx-1">↑↓</span> and Enter. Order decides the order in the menu.
      </p>

      <div className="space-y-1 mb-3">
        {items.map((item, index) => (
          <div
            key={`${index}-${item.tag}`}
            draggable
            onDragStart={() => setDragIndex(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-2 p-2 rounded-lg border border-dark-border bg-dark-elevated group ${
              dragIndex === index ? 'opacity-50' : ''
            }`}
          >
            <GripVertical className="w-4 h-4 text-dark-text-muted cursor-grab shrink-0" />
            {item.tag && (
              <span className="shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wide bg-dark-surface border border-dark-border text-dark-text-muted">
                {item.tag}
              </span>
            )}
            <span className="text-sm text-dark-text-secondary flex-1 truncate" title={snippetLabel(item)}>
              {snippetLabel(item)}
            </span>
            <button
              onClick={() => openForm(index)}
              className="text-dark-text-muted hover:text-dark-text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              title="Edit snippet"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => save(items.filter((_, i) => i !== index))}
              className="text-dark-text-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              title="Delete snippet"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-dark-text-muted italic p-2">
            No snippets yet. Add one and it shows up behind <code>/</code> in every mail editor.
          </p>
        )}
      </div>

      <Button
        onClick={() => openForm(null)}
        variant="secondary"
        size="sm"
        disabled={items.length >= MAX_SNIPPETS}
        title={items.length >= MAX_SNIPPETS ? `At most ${MAX_SNIPPETS} snippets` : undefined}
      >
        <Plus className="w-4 h-4 mr-1" />
        Add snippet
      </Button>

      {editing && (
        <SnippetModal
          key={editing.seq}
          snippet={editing.index === null ? null : items[editing.index]}
          onClose={() => setEditing(null)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

export function ProfilePage() {
  const [activeTab, setActiveTab] = useState<'account' | 'bands' | 'templates' | 'settings' | 'dashboard' | 'data'>(
    'account'
  );
  const [isDownloadingVenues, setIsDownloadingVenues] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templateModalMode, setTemplateModalMode] = useState<'create' | 'edit'>('create');
  const [isBandModalOpen, setIsBandModalOpen] = useState(false);
  const [editingBand, setEditingBand] = useState<Band | null>(null);
  const [bandModalMode, setBandModalMode] = useState<'create' | 'edit'>('create');
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteBandConfirm, setDeleteBandConfirm] = useState<string | null>(null);
  const [isDeletingBand, setIsDeletingBand] = useState(false);
  // Band reordering: the list as the pointer has rearranged it, held locally until the
  // server's own list comes back in that order (docs/BAND_ORDER.md).
  const [draggedBands, setDraggedBands] = useState<Band[] | null>(null);
  const [bandDragIndex, setBandDragIndex] = useState<number | null>(null);
  const [deleteTemplateConfirm, setDeleteTemplateConfirm] = useState<string | null>(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [dataDeleteAction, setDataDeleteAction] = useState<'all' | 'emails' | 'contacts' | 'venues-and-contacts' | null>(null);
  const [isDeletingData, setIsDeletingData] = useState(false);
  const [dataDeleteConfirmText, setDataDeleteConfirmText] = useState('');
  const [autoSyncEnabled, setAutoSyncEnabled] = useAutoSyncEnabled();
  const [deletingImportId, setDeletingImportId] = useState<string | null>(null);
  const [showRevokeGmail, setShowRevokeGmail] = useState(false);
  const [isRevokingGmail, setIsRevokingGmail] = useState(false);
  const { logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Handle redirect back from Gmail OAuth connect flow
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gmail = params.get('gmail');
    if (gmail === 'connected' || gmail === 'error') {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      // Keep the AuthContext user (used by the Navbar sync button) in sync so
      // gmail_permissions_granted updates without requiring a re-login.
      refreshUser();
      navigate('/profile', { replace: true });
    }
  }, [location.search, navigate, queryClient, refreshUser]);

  const updateSettingsMutation = useMutation({
    mutationFn: authAPI.updateSettings,
    onSuccess: (response) => {
      queryClient.setQueryData(['current-user'], response.data);
    },
  });

  // Fetch current user
  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const response = await authAPI.me();
      return response.data;
    },
  });

  // Timezone options, with the saved zone guaranteed to be selectable even if the
  // browser's zone list doesn't carry it (older browsers report a shorter list).
  const timezoneOptions = useMemo(() => {
    const current = resolveUserTimezone(user?.settings?.timezone);
    const zones = listTimezones();
    return zones.includes(current) ? zones : [current, ...zones];
  }, [user?.settings?.timezone]);

  // Fetch email templates
  const { data: templates = [], refetch: refetchTemplates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const response = await emailTemplatesAPI.list();
      return response.data.items;
    },
  });

  // Fetch bands
  const { data: bands = [], refetch: refetchBands, isLoading: isLoadingBands } = useQuery({
    queryKey: ['bands'],
    queryFn: async () => {
      const response = await bandsAPI.list();
      return response.data.items;
    },
  });

  // What the Bands tab lists: the saved order, with a drag in flight applied on top.
  const orderedBands = draggedBands ?? bands;

  // Fetch import history
  const { data: imports = [], refetch: refetchImports, isLoading: isLoadingImports } = useQuery({
    queryKey: ['import-history'],
    queryFn: async () => {
      const response = await csvImportAPI.history();
      return response.data;
    },
    enabled: activeTab === 'data',
  });

  const handleDeleteImport = async () => {
    if (!deletingImportId || dataDeleteConfirmText !== 'DELETE') return;
    setIsDeletingData(true);
    try {
      await csvImportAPI.deleteImport(deletingImportId);
      await refetchImports();
      setDeletingImportId(null);
      setDataDeleteConfirmText('');
    } catch (error) {
      console.error('Failed to delete import:', error);
    } finally {
      setIsDeletingData(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setIsDeletingAccount(true);
    try {
      await authAPI.deleteAccount();
      sessionStorage.removeItem('access_token');
      window.location.href = '/login';
    } catch (error) {
      console.error('Failed to delete account:', error);
      setIsDeletingAccount(false);
    }
  };

  const dataDeleteLabels: Record<string, { title: string; description: string; button: string }> = {
    'all': {
      title: 'Delete All Data',
      description: 'This will permanently delete all your venues, contacts, bands, concerts, templates, and todos. Your account and preferences will be kept.',
      button: 'Delete All Data',
    },
    'emails': {
      title: 'Delete All Emails',
      description: 'This will permanently delete all synced email conversations from your contacts.',
      button: 'Delete Emails',
    },
    'contacts': {
      title: 'Delete All Contacts',
      description: 'This will permanently delete all your contacts and their email history.',
      button: 'Delete Contacts',
    },
    'venues-and-contacts': {
      title: 'Delete Venues & Contacts',
      description: 'This will permanently delete all your venues, venue notes, and contacts.',
      button: 'Delete Venues & Contacts',
    },
  };

  const handleDeleteData = async () => {
    if (!dataDeleteAction || dataDeleteConfirmText !== 'DELETE') return;
    setIsDeletingData(true);
    try {
      const actions = {
        'all': authAPI.deleteAllData,
        'emails': authAPI.deleteEmails,
        'contacts': authAPI.deleteContacts,
        'venues-and-contacts': authAPI.deleteVenuesAndContacts,
      };
      await actions[dataDeleteAction]();
      setDataDeleteAction(null);
      setDataDeleteConfirmText('');
    } catch (error) {
      console.error('Failed to delete data:', error);
    } finally {
      setIsDeletingData(false);
    }
  };

  const handleCreateTemplate = async (templateData: TemplateFormData) => {
    try {
      await emailTemplatesAPI.create({
        ...templateData,
        band_ids: templateData.band_ids || [],
      });
      await refetchTemplates();
    } catch (error) {
      console.error('Failed to create template:', error);
      throw error;
    }
  };

  const handleUpdateTemplate = async (templateData: TemplateFormData) => {
    if (editingTemplate) {
      try {
        await emailTemplatesAPI.update(editingTemplate._id, templateData);
        await refetchTemplates();
        setEditingTemplate(null);
      } catch (error) {
        console.error('Failed to update template:', error);
        throw error;
      }
    }
  };

  const handleTemplateSubmit = async (templateData: TemplateFormData) => {
    try {
      if (templateModalMode === 'edit') {
        await handleUpdateTemplate(templateData);
      } else {
        await handleCreateTemplate(templateData);
      }
    } catch (error) {
      console.error('Failed to submit template:', error);
      throw error;
    }
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setTemplateModalMode('edit');
    setIsTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setEditingTemplate(null);
    setTemplateModalMode('create');
  };

  // Band handlers
  const handleCreateBand = async (bandData: BandFormData) => {
    try {
      await bandsAPI.create(bandData);
      await refetchBands();
    } catch (error) {
      console.error('Failed to create band:', error);
      throw error;
    }
  };

  const handleUpdateBand = async (bandData: BandFormData) => {
    if (editingBand) {
      try {
        await bandsAPI.update(editingBand._id, bandData);
        await refetchBands();
        setEditingBand(null);
      } catch (error) {
        console.error('Failed to update band:', error);
        throw error;
      }
    }
  };

  const handleBandSubmit = async (bandData: BandFormData) => {
    try {
      if (bandModalMode === 'edit') {
        await handleUpdateBand(bandData);
      } else {
        await handleCreateBand(bandData);
      }
    } catch (error) {
      console.error('Failed to submit band:', error);
      throw error;
    }
  };

  const handleEditBand = (band: Band) => {
    setEditingBand(band);
    setBandModalMode('edit');
    setIsBandModalOpen(true);
  };

  const handleDeleteBand = async (bandId: string) => {
    try {
      setIsDeletingBand(true);
      await bandsAPI.delete(bandId);
      await refetchBands();
    } catch (error) {
      console.error('Failed to delete band:', error);
    } finally {
      setIsDeletingBand(false);
      setDeleteBandConfirm(null);
    }
  };

  const handleCloseBandModal = () => {
    setIsBandModalOpen(false);
    setEditingBand(null);
    setBandModalMode('create');
  };

  // Band reordering. The order set here is the order bands are listed in everywhere else —
  // the Venues favourites and interest chips above all (docs/BAND_ORDER.md).
  const handleBandDragStart = (index: number) => {
    setBandDragIndex(index);
    setDraggedBands(bands);
  };

  const handleBandDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (bandDragIndex === null || bandDragIndex === index) return;
    const reordered = [...orderedBands];
    const [moved] = reordered.splice(bandDragIndex, 1);
    reordered.splice(index, 0, moved);
    setDraggedBands(reordered);
    setBandDragIndex(index);
  };

  const handleBandDragEnd = async () => {
    const reordered = draggedBands;
    setBandDragIndex(null);
    if (!reordered) return;
    const ids = reordered.map((b) => b._id);
    if (ids.join() === bands.map((b) => b._id).join()) {
      setDraggedBands(null);
      return;
    }
    // Patch the cache rather than wait for the round trip, or the cards snap back to the old
    // order for a frame. Every page reads the same ['bands'] query, so they all follow.
    queryClient.setQueryData(['bands'], reordered.map((b, i) => ({ ...b, display_order: i })));
    try {
      await bandsAPI.reorder(ids);
      await queryClient.invalidateQueries({ queryKey: ['bands'] });
    } catch (error) {
      console.error('Failed to reorder bands:', error);
      await refetchBands();
    } finally {
      setDraggedBands(null);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      setIsDeletingTemplate(true);
      await emailTemplatesAPI.delete(templateId);
      await refetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
    } finally {
      setIsDeletingTemplate(false);
      setDeleteTemplateConfirm(null);
    }
  };

  const handleDownloadVenues = async () => {
    setIsDownloadingVenues(true);
    try {
      const response = await venuesAPI.download();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my_venues.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download venues:', error);
    } finally {
      setIsDownloadingVenues(false);
    }
  };

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-2xl) mx-auto w-full p-3 sm:p-4 md:p-6 flex-1">
        <div className="mb-6 animate-slideDown">
          <h1 className="text-2xl font-bold text-dark-text-primary mb-2 tracking-tight flex items-center gap-2">
            <UserIcon className="w-6 h-6" />
            Profile
          </h1>
          <p className="text-sm text-dark-text-secondary">
            Manage your account settings and preferences
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3">
            <div className="card space-y-2 animate-scaleIn">
              <button
                onClick={() => setActiveTab('account')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === 'account'
                    ? 'bg-linear-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                    : 'hover:bg-dark-elevated text-dark-text-secondary hover:scale-105'
                }`}
              >
                <UserIcon className="w-5 h-5" />
                Account
              </button>

              <button
                onClick={() => setActiveTab('bands')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === 'bands'
                    ? 'bg-linear-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                    : 'hover:bg-dark-elevated text-dark-text-secondary hover:scale-105'
                }`}
              >
                <Music className="w-5 h-5" />
                Bands
              </button>

              <button
                onClick={() => setActiveTab('templates')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === 'templates'
                    ? 'bg-linear-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                    : 'hover:bg-dark-elevated text-dark-text-secondary hover:scale-105'
                }`}
              >
                <Mail className="w-5 h-5" />
                Templates
              </button>

              <button
                onClick={() => navigate('/mailing-lists')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 hover:bg-dark-elevated text-dark-text-secondary hover:scale-105"
              >
                <Send className="w-5 h-5" />
                Lists
              </button>

              <button
                onClick={() => setActiveTab('settings')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === 'settings'
                    ? 'bg-linear-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                    : 'hover:bg-dark-elevated text-dark-text-secondary hover:scale-105'
                }`}
              >
                <Settings className="w-5 h-5" />
                Settings
              </button>

              <hr className="border-dark-border my-2" />

              <button
                onClick={() => setActiveTab('dashboard')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === 'dashboard'
                    ? 'bg-linear-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                    : 'hover:bg-dark-elevated text-dark-text-secondary hover:scale-105'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                Dashboard
              </button>

              <button
                onClick={() => setActiveTab('data')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === 'data'
                    ? 'bg-linear-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                    : 'hover:bg-dark-elevated text-dark-text-secondary hover:scale-105'
                }`}
              >
                <Database className="w-5 h-5" />
                My Data
              </button>

              <hr className="border-dark-border my-2" />

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 hover:bg-red-900/20 text-red-400 hover:scale-105 hover:shadow-lg hover:shadow-red-900/30"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9 animate-scaleIn">
            <div className="card">
              {activeTab === 'account' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-dark-text-primary">
                    Account Information
                  </h2>

                  {isLoadingUser ? (
                    <div className="space-y-6">
                      <div className="flex items-center gap-4 p-6 bg-dark-elevated rounded-xl">
                        <Skeleton variant="circular" width="80px" height="80px" />
                        <div className="space-y-2">
                          <Skeleton width="200px" height="24px" />
                          <Skeleton width="60px" height="20px" />
                        </div>
                      </div>
                      <div className="space-y-4">
                        <Skeleton width="100%" height="40px" />
                        <Skeleton width="100%" height="40px" />
                        <Skeleton width="150px" height="20px" />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-4 p-6 bg-dark-elevated rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <div className="w-20 h-20 bg-linear-to-br from-primary-600 to-primary-800 rounded-full flex items-center justify-center shadow-lg shadow-primary-900/50">
                          <UserIcon className="w-10 h-10 text-white" />
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-dark-text-primary">
                            {user?.email}
                          </div>
                          <Badge variant="info">{user?.tier || 'free'}</Badge>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <Input
                          label="Email"
                          value={user?.email || ''}
                          disabled
                          className="bg-dark-bg"
                        />

                        <Input
                          label="Plan"
                          value={user?.tier || 'free'}
                          disabled
                          className="bg-dark-bg"
                        />

                        <div>
                          <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                            Member Since
                          </label>
                          <div className="text-dark-text-primary">
                            {user?.created_at
                              ? new Date(user.created_at).toLocaleDateString()
                              : 'Unknown'}
                          </div>
                        </div>

                        <div className="pt-4 border-t border-dark-border">
                          <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                            Gmail Integration
                          </label>
                          <div className="flex items-center justify-between p-4 bg-dark-elevated rounded-xl">
                            <div className="flex items-center gap-3">
                              {user?.gmail_permissions_granted ? (
                                <>
                                  <CheckCircle className="w-5 h-5 text-green-400" />
                                  <div>
                                    <div className="text-dark-text-primary font-medium">Gmail permissions granted</div>
                                    <div className="text-sm text-dark-text-muted">You can send and receive emails through the app</div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-5 h-5 text-amber-400" />
                                  <div>
                                    <div className="text-dark-text-primary font-medium">Gmail permissions not granted</div>
                                    <div className="text-sm text-dark-text-muted">Grant permissions to send and sync emails</div>
                                  </div>
                                </>
                              )}
                            </div>
                            {user?.gmail_permissions_granted ? (
                              <Button
                                variant="ghost"
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                onClick={() => setShowRevokeGmail(true)}
                              >
                                Revoke
                              </Button>
                            ) : (
                              <Button
                                variant="secondary"
                                onClick={async () => {
                                  const response = await authAPI.connectGmail();
                                  window.location.href = response.data.authorization_url;
                                }}
                              >
                                <Mail className="w-4 h-4" />
                                Grant Permissions
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="pt-6 mt-6 border-t border-red-900/30">
                        <h3 className="text-lg font-semibold text-red-400 mb-2">Danger Zone</h3>
                        <p className="text-sm text-dark-text-muted mb-4">
                          Permanently delete your account and all associated data. This action cannot be undone.
                        </p>
                        <Button
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 hover:bg-red-900/20"
                          onClick={() => setIsDeleteAccountModalOpen(true)}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Account
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'bands' && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold text-dark-text-primary">
                        My Bands
                      </h2>
                      <Button onClick={() => {
                        setBandModalMode('create');
                        setEditingBand(null);
                        setIsBandModalOpen(true);
                      }}>
                        <Music className="w-4 h-4" />
                        New Band
                      </Button>
                    </div>
                    <p className="text-xs text-dark-text-muted mt-2">
                      Drag by the handle to reorder. This order is the order the bands appear in
                      everywhere else — the Venues table's favourite and interest chips, the band
                      pickers and the filters.
                    </p>
                  </div>

                  {isLoadingBands ? (
                    <div className="space-y-3">
                      {[1, 2].map((i) => (
                        <div key={i} className="p-5 bg-dark-elevated rounded-xl border border-dark-border">
                          <Skeleton width="200px" height="20px" className="mb-2" />
                          <Skeleton width="300px" height="16px" />
                        </div>
                      ))}
                    </div>
                  ) : bands.length === 0 ? (
                    <div className="text-center py-12 text-dark-text-muted">
                      No bands yet. Add your first band to get started.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {orderedBands.map((band, index) => (
                        <div
                          key={band._id}
                          draggable
                          onDragStart={() => handleBandDragStart(index)}
                          onDragOver={(e) => handleBandDragOver(e, index)}
                          onDragEnd={handleBandDragEnd}
                          className={`p-5 bg-dark-elevated rounded-xl border border-dark-border hover:border-primary-600 transition-colors duration-300 hover:shadow-lg ${
                            bandDragIndex === index ? 'opacity-50' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <GripVertical className="w-4 h-4 mt-1 text-dark-text-muted cursor-grab shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-dark-text-primary mb-2">
                                {band.name}
                                {band.short_name && (
                                  <span className="ml-2 px-1.5 py-0.5 rounded-sm text-[10px] align-middle bg-dark-surface border border-dark-border text-dark-text-muted">
                                    {band.short_name}
                                  </span>
                                )}
                              </h3>
                              {band.styles && band.styles.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {band.styles.map((style) => (
                                    <span
                                      key={style}
                                      className="inline-flex px-2 py-0.5 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-xs font-medium"
                                    >
                                      {style}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditBand(band);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteBandConfirm(band._id);
                                }}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'templates' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-dark-text-primary">
                      Email Templates
                    </h2>
                    <Button onClick={() => {
                      setTemplateModalMode('create');
                      setEditingTemplate(null);
                      setIsTemplateModalOpen(true);
                    }}>
                      <Mail className="w-4 h-4" />
                      New Template
                    </Button>
                  </div>

                  {isLoadingTemplates ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="p-5 bg-dark-elevated rounded-xl border border-dark-border">
                          <Skeleton width="200px" height="20px" className="mb-2" />
                          <Skeleton width="300px" height="16px" className="mb-2" />
                          <Skeleton width="100%" height="14px" />
                        </div>
                      ))}
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="text-center py-12 text-dark-text-muted">
                      No templates yet. Create your first template to get started.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {templates.map((template) => {
                        const templateBands = bands.filter(b => template.band_ids?.includes(b._id));
                        return (
                          <div
                            key={template._id}
                            className="p-5 bg-dark-elevated rounded-xl border border-dark-border hover:border-primary-600 transition-all duration-300 cursor-pointer hover:scale-[1.02] hover:shadow-lg"
                            onClick={() => handleEditTemplate(template)}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-dark-text-primary mb-1">
                                  {template.name}
                                </h3>
                                <div className="text-sm text-dark-text-secondary mb-2">
                                  Subject: {template.subject}
                                </div>
                                {templateBands.length > 0 ? (
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {templateBands.map((band) => (
                                      <span
                                        key={band._id}
                                        className="inline-flex px-2 py-0.5 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-xs font-medium"
                                      >
                                        {band.name}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-dark-text-muted italic mb-2">
                                    Generic template (all bands)
                                  </div>
                                )}
                                <div className="text-xs text-dark-text-muted">
                                  {(template.email_text || '').replace(/<[^>]*>/g, '').substring(0, 150)}...
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTemplate(template);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTemplateConfirm(template._id);
                                  }}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'dashboard' && (
                <DashboardAnalytics />
              )}

              {activeTab === 'data' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-dark-text-primary">
                    My Data
                  </h2>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-5 bg-dark-elevated rounded-xl border border-dark-border">
                      <div>
                        <h3 className="font-semibold text-dark-text-primary mb-1">Export Venues</h3>
                        <p className="text-sm text-dark-text-muted">
                          Download all your venues and their associated contacts as a CSV file.
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={handleDownloadVenues}
                        isLoading={isDownloadingVenues}
                      >
                        <Download className="w-4 h-4" />
                        Download CSV
                      </Button>
                    </div>
                  </div>

                  <div className="pt-6 mt-2 border-t border-red-900/30">
                    <h3 className="text-lg font-semibold text-red-400 mb-2">Danger Zone</h3>
                    <p className="text-sm text-dark-text-muted mb-4">
                      These actions permanently delete data and cannot be undone.
                    </p>
                    <div className="space-y-3">
                      {/* Import history - undo per import */}
                      {isLoadingImports ? (
                        <div className="space-y-3">
                          {[1, 2].map((i) => (
                            <div key={i} className="p-4 bg-dark-elevated rounded-xl border border-red-900/30">
                              <Skeleton width="200px" height="18px" className="mb-2" />
                              <Skeleton width="300px" height="14px" />
                            </div>
                          ))}
                        </div>
                      ) : imports.length > 0 && (
                        <>
                          <h4 className="text-sm font-medium text-dark-text-secondary uppercase tracking-wider">Undo Imports</h4>
                          {imports.map((imp) => (
                            <div key={imp.id} className="flex items-center justify-between p-4 bg-dark-elevated rounded-xl border border-red-900/30">
                              <div className="flex items-center gap-3">
                                <Upload className="w-5 h-5 text-dark-text-muted" />
                                <div>
                                  <h4 className="font-medium text-dark-text-primary">{imp.filename}</h4>
                                  <p className="text-sm text-dark-text-muted">
                                    {new Date(imp.imported_at).toLocaleDateString()} — {imp.venues_created} venue{imp.venues_created !== 1 ? 's' : ''}, {imp.contacts_created} contact{imp.contacts_created !== 1 ? 's' : ''}
                                    {!!imp.contacts_linked && `, ${imp.contacts_linked} existing contact${imp.contacts_linked !== 1 ? 's' : ''} linked`}
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                className="text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 hover:bg-red-900/20 shrink-0 ml-4"
                                onClick={() => setDeletingImportId(imp.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Venues & Contacts
                              </Button>
                            </div>
                          ))}
                          <hr className="border-dark-border my-1" />
                        </>
                      )}

                      <div className="flex items-center justify-between p-4 bg-dark-elevated rounded-xl border border-red-900/30">
                        <div>
                          <h4 className="font-medium text-dark-text-primary">Delete all my data</h4>
                          <p className="text-sm text-dark-text-muted">Removes venues, contacts, bands, concerts, templates, and todos. Keeps your account and preferences.</p>
                        </div>
                        <Button
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 hover:bg-red-900/20 shrink-0 ml-4"
                          onClick={() => setDataDeleteAction('all')}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete All Data
                        </Button>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-dark-elevated rounded-xl border border-red-900/30">
                        <div>
                          <h4 className="font-medium text-dark-text-primary">Delete my emails</h4>
                          <p className="text-sm text-dark-text-muted">Removes all synced email conversations from your contacts.</p>
                        </div>
                        <Button
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 hover:bg-red-900/20 shrink-0 ml-4"
                          onClick={() => setDataDeleteAction('emails')}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Emails
                        </Button>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-dark-elevated rounded-xl border border-red-900/30">
                        <div>
                          <h4 className="font-medium text-dark-text-primary">Delete my contacts</h4>
                          <p className="text-sm text-dark-text-muted">Removes all contacts and their email history.</p>
                        </div>
                        <Button
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 hover:bg-red-900/20 shrink-0 ml-4"
                          onClick={() => setDataDeleteAction('contacts')}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Contacts
                        </Button>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-dark-elevated rounded-xl border border-red-900/30">
                        <div>
                          <h4 className="font-medium text-dark-text-primary">Delete my venues and contacts</h4>
                          <p className="text-sm text-dark-text-muted">Removes all venues, their notes, and all contacts.</p>
                        </div>
                        <Button
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 hover:bg-red-900/20 shrink-0 ml-4"
                          onClick={() => setDataDeleteAction('venues-and-contacts')}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Venues & Contacts
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-dark-text-primary">
                    Settings
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-md font-semibold text-dark-text-primary mb-4">
                        Email Sync
                      </h3>
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={autoSyncEnabled}
                            onChange={(e) => setAutoSyncEnabled(e.target.checked)}
                            className="w-4 h-4 rounded-sm border-dark-border bg-dark-elevated"
                          />
                          <span className="text-dark-text-secondary">
                            Automatic email sync
                          </span>
                        </label>
                        <p className="text-xs text-dark-text-muted">
                          {autoSyncEnabled
                            ? 'Emails are synced automatically when you open the app and every 5 minutes.'
                            : 'Automatic sync is disabled. Use the sync button in the navbar to sync manually.'}
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-md font-semibold text-dark-text-primary mb-4">
                        Timezone
                      </h3>
                      <div className="space-y-2">
                        <select
                          value={resolveUserTimezone(user?.settings?.timezone)}
                          onChange={(e) => updateSettingsMutation.mutate({ timezone: e.target.value })}
                          disabled={updateSettingsMutation.isPending}
                          className="w-full max-w-md px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500"
                        >
                          {timezoneOptions.map((tz) => (
                            <option key={tz} value={tz}>
                              {tz}
                              {timezoneOffsetLabel(tz) && ` (${timezoneOffsetLabel(tz)})`}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-dark-text-muted">
                          Send windows for batch emails use this timezone. Detected from your browser
                          ({getBrowserTimezone()}) when you first signed in.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-md font-semibold text-dark-text-primary mb-4">
                        Email Bounce Handling
                      </h3>
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={user?.settings?.auto_expire_bounced_contacts ?? true}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              updateSettingsMutation.mutate({
                                auto_expire_bounced_contacts: checked,
                                bounce_create_review_task: checked ? (user?.settings?.bounce_create_review_task ?? true) : false,
                              });
                            }}
                            className="w-4 h-4 rounded-sm border-dark-border bg-dark-elevated"
                          />
                          <span className="text-dark-text-secondary">
                            Automatically mark contacts as expired when emails bounce
                          </span>
                        </label>
                        <label className={`flex items-center gap-3 ml-7 ${user?.settings?.auto_expire_bounced_contacts !== false ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                          <input
                            type="checkbox"
                            checked={(user?.settings?.bounce_create_review_task ?? true) && (user?.settings?.auto_expire_bounced_contacts !== false)}
                            disabled={user?.settings?.auto_expire_bounced_contacts === false}
                            onChange={(e) => updateSettingsMutation.mutate({
                              bounce_create_review_task: e.target.checked,
                            })}
                            className="w-4 h-4 rounded-sm border-dark-border bg-dark-elevated"
                          />
                          <span className="text-dark-text-secondary">
                            Create a review task when a bounce is detected
                          </span>
                        </label>
                        <p className="text-xs text-dark-text-muted">
                          {user?.settings?.auto_expire_bounced_contacts !== false
                            ? (user?.settings?.bounce_create_review_task !== false
                              ? 'When a bounce is detected, the contact will be flagged as expired and a review task will be created.'
                              : 'When a bounce is detected, the contact will be flagged as expired. No review task will be created.')
                            : 'Bounce detection is disabled. Bounced emails will be ignored during sync.'}
                        </p>
                        {user?.settings?.auto_expire_bounced_contacts !== false && (
                          <p className="text-xs text-dark-text-muted">
                            A bounce notification is never shown in the app, so it is marked as read in Gmail.
                          </p>
                        )}
                      </div>
                    </div>

                    <FollowUpTodoSetting
                      key={`todo-days-${user?._id ?? 'loading'}`}
                      days={user?.settings?.default_todo_days ?? FALLBACK_TODO_DAYS}
                      onSave={(value) => updateSettingsMutation.mutate({ default_todo_days: value })}
                      isSaving={updateSettingsMutation.isPending}
                    />

                    <DefaultChecklistEditor
                      key={user?._id ?? 'loading'}
                      checklist={user?.settings?.default_concert_checklist ?? []}
                      onSave={(items) => updateSettingsMutation.mutate({ default_concert_checklist: items })}
                    />

                    <EmailSnippetsEditor
                      key={`snippets-${user?._id ?? 'loading'}`}
                      snippets={user?.settings?.email_snippets ?? []}
                      onSave={(snippets) => updateSettingsMutation.mutate({ email_snippets: snippets })}
                    />

                    <ThemeSelector />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <TemplateModal
        isOpen={isTemplateModalOpen}
        onClose={handleCloseTemplateModal}
        onSubmit={handleTemplateSubmit}
        template={editingTemplate}
        mode={templateModalMode}
        bands={bands}
      />

      <BandModal
        isOpen={isBandModalOpen}
        onClose={handleCloseBandModal}
        onSubmit={handleBandSubmit}
        band={editingBand}
        mode={bandModalMode}
      />

      <ConfirmDialog
        isOpen={!!deleteBandConfirm}
        onClose={() => setDeleteBandConfirm(null)}
        onConfirm={() => deleteBandConfirm && handleDeleteBand(deleteBandConfirm)}
        title="Delete Band?"
        message="This action cannot be undone. Are you sure you want to delete this band?"
        confirmLabel="Delete Band"
        danger
        isLoading={isDeletingBand}
      />

      <ConfirmDialog
        isOpen={!!deleteTemplateConfirm}
        onClose={() => setDeleteTemplateConfirm(null)}
        onConfirm={() => deleteTemplateConfirm && handleDeleteTemplate(deleteTemplateConfirm)}
        title="Delete Template?"
        message="This action cannot be undone. Are you sure you want to delete this template?"
        confirmLabel="Delete Template"
        danger
        isLoading={isDeletingTemplate}
      />

      <ConfirmDialog
        isOpen={showRevokeGmail}
        onClose={() => setShowRevokeGmail(false)}
        onConfirm={async () => {
          setIsRevokingGmail(true);
          try {
            await authAPI.revokeGmail();
            queryClient.invalidateQueries({ queryKey: ['current-user'] });
            setShowRevokeGmail(false);
          } finally {
            setIsRevokingGmail(false);
          }
        }}
        title="Revoke Gmail Permissions?"
        message="This will disconnect your Gmail account. You will no longer be able to send or sync emails until you re-authorize."
        confirmLabel="Revoke Permissions"
        danger
        isLoading={isRevokingGmail}
      />

      {dataDeleteAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black backdrop-blur-xs"
            onClick={() => {
              setDataDeleteAction(null);
              setDataDeleteConfirmText('');
            }}
          />
          <div className="relative bg-dark-card border border-dark-border rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scaleIn">
            <h2 className="text-xl font-semibold text-red-400 mb-2">{dataDeleteLabels[dataDeleteAction].title}</h2>
            <p className="text-sm text-dark-text-secondary mb-4">
              {dataDeleteLabels[dataDeleteAction].description}
            </p>
            <p className="text-sm text-dark-text-secondary mb-4">
              Type <span className="font-mono font-bold text-red-400">DELETE</span> to confirm.
            </p>
            <Input
              value={dataDeleteConfirmText}
              onChange={(e) => setDataDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="mb-4"
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setDataDeleteAction(null);
                  setDataDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="ghost"
                className="bg-red-600 hover:bg-red-700 text-white border-none"
                disabled={dataDeleteConfirmText !== 'DELETE' || isDeletingData}
                onClick={handleDeleteData}
              >
                <Trash2 className="w-4 h-4" />
                {isDeletingData ? 'Deleting...' : dataDeleteLabels[dataDeleteAction].button}
              </Button>
            </div>
          </div>
        </div>
      )}

      {deletingImportId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black backdrop-blur-xs"
            onClick={() => {
              setDeletingImportId(null);
              setDataDeleteConfirmText('');
            }}
          />
          <div className="relative bg-dark-card border border-dark-border rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scaleIn">
            <h2 className="text-xl font-semibold text-red-400 mb-2">Delete Import Data</h2>
            <p className="text-sm text-dark-text-secondary mb-4">
              This will permanently delete the venues and contacts created by this import. Contacts
              still linked to another venue, and existing contacts the import only linked, are kept.
            </p>
            <p className="text-sm text-dark-text-secondary mb-4">
              Type <span className="font-mono font-bold text-red-400">DELETE</span> to confirm.
            </p>
            <Input
              value={dataDeleteConfirmText}
              onChange={(e) => setDataDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="mb-4"
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setDeletingImportId(null);
                  setDataDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="ghost"
                className="bg-red-600 hover:bg-red-700 text-white border-none"
                disabled={dataDeleteConfirmText !== 'DELETE' || isDeletingData}
                onClick={handleDeleteImport}
              >
                <Trash2 className="w-4 h-4" />
                {isDeletingData ? 'Deleting...' : 'Delete Venues & Contacts'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isDeleteAccountModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black backdrop-blur-xs"
            onClick={() => {
              setIsDeleteAccountModalOpen(false);
              setDeleteConfirmText('');
            }}
          />
          <div className="relative bg-dark-card border border-dark-border rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scaleIn">
            <h2 className="text-xl font-semibold text-red-400 mb-2">Delete Account</h2>
            <p className="text-sm text-dark-text-secondary mb-4">
              This will permanently delete your account and all your data including contacts, venues, bands, concerts, templates, and reminders.
            </p>
            <p className="text-sm text-dark-text-secondary mb-4">
              Type <span className="font-mono font-bold text-red-400">DELETE</span> to confirm.
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="mb-4"
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsDeleteAccountModalOpen(false);
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="ghost"
                className="bg-red-600 hover:bg-red-700 text-white border-none"
                disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount}
                onClick={handleDeleteAccount}
              >
                <Trash2 className="w-4 h-4" />
                {isDeletingAccount ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
